// Persistent user search preferences (category, subcategory, state, city,
// bairro, query e flags como "online" e maxKm) usados para evitar perder
// os filtros entre páginas. Armazenado em localStorage — nunca vai pro
// servidor.
//
// Convenção: a chave é única (`forfada:userPrefs`). Filtros que não
// existirem na chamada de `store` são preservados do estado anterior
// (merge não-destrutivo).

import { sanitizeLocationValue } from "@/lib/locationSanitizers";

const KEY = "forfada:userPrefs";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Schema atual (versionado pra permitir migrações silenciosas no futuro).
const DEFAULTS = {
  category: "",
  subcategory: "",
  state: "",
  city: "",
  neighborhood: "",
  q: "",
  online: false,
  // Distância máxima em km quando o usuário compartilhou geolocalização.
  // `null` significa "sem limite" (não filtrar).
  maxKm: null as number | null,
  attributes: {} as Record<string, string[]>,
};

export interface UserPrefs {
  category: string;
  subcategory: string;
  state: string;
  city: string;
  neighborhood: string;
  q: string;
  online: boolean;
  maxKm: number | null;
  attributes: Record<string, string[]>;
  savedAt: number;
}

function _sanitize(parsed: unknown): UserPrefs | null {
  if (!parsed || typeof parsed !== "object") return null;
  const p = parsed as Record<string, unknown>;
  return {
    category: typeof p.category === "string" ? p.category : "",
    subcategory: typeof p.subcategory === "string" ? p.subcategory : "",
    state: sanitizeLocationValue(p.state),
    city: sanitizeLocationValue(p.city),
    neighborhood: sanitizeLocationValue(p.neighborhood),
    q: typeof p.q === "string" ? p.q : "",
    online: !!p.online,
    maxKm: typeof p.maxKm === "number" && p.maxKm > 0 ? p.maxKm : null,
    attributes: (() => {
      if (!p.attributes || typeof p.attributes !== "object") return {};
      const result: Record<string, string[]> = {};
      for (const [key, val] of Object.entries(p.attributes)) {
        if (Array.isArray(val) && val.every((v) => typeof v === "string")) {
          result[key] = val;
        }
      }
      return result;
    })(),
    savedAt: typeof p.savedAt === "number" ? p.savedAt : Date.now(),
  };
}

export function loadUserPrefs(): UserPrefs | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof (parsed as { savedAt?: unknown })?.savedAt === "number" && Date.now() - (parsed as { savedAt: number }).savedAt > TTL_MS) {
      try { localStorage.removeItem(KEY); } catch { /* ignore */ }
      return null;
    }
    return _sanitize(parsed);
  } catch {
    /* ignore */
  }
  return null;
}

// Merge não-destrutivo: campos ausentes em `next` mantêm o valor anterior.
// Use `null`/`""` explícito para LIMPAR um filtro (não basta não passar).
export function storeUserPrefs(next: Partial<UserPrefs>): UserPrefs | null {
  try {
    const prev = loadUserPrefs() || { ...DEFAULTS };
    const merged: UserPrefs = {
      ..._sanitize({ ...prev, ...next })!,
      savedAt: Date.now(),
    };
    localStorage.setItem(KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return null;
  }
}

export function clearUserPrefs(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** True quando temos no mínimo: category + state + city. */
export function hasMinimumPrefs(prefs: UserPrefs | null): boolean {
  return !!(prefs && prefs.category && prefs.state && prefs.city);
}